interface AvatarProps {
  name: string
  size?: number
  /** Explicit fill colour. When omitted a stable colour is derived from the
   *  name so each person reads as a consistent hue across surfaces. Pass
   *  `null` to fall back to the neutral surface chip. */
  color?: string | null
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2)
  return parts
    .map((part) => part[0]?.toUpperCase() ?? '')
    .filter(Boolean)
    .join('')
}

// Palette mirrors the Lumen prototype's persona swatches — saturated enough
// to read against white at avatar size, distinct from each other at a glance.
const AVATAR_PALETTE = [
  '#d63384',
  '#1f6feb',
  '#1a7f37',
  '#8957e5',
  '#b45309',
  '#4f46e5',
  '#ea580c',
  '#0891b2',
  '#be123c',
  '#0f766e',
] as const

function colorForName(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0
  }
  const index = Math.abs(hash) % AVATAR_PALETTE.length
  return AVATAR_PALETTE[index] as string
}

export function Avatar({ name, size = 32, color }: AvatarProps): JSX.Element {
  const initials = initialsOf(name) || '?'
  // `color === null` → neutral chip; `undefined` → derive from name; string → use it.
  const fill = color === null ? null : (color ?? colorForName(name))
  return (
    <span
      aria-label={name}
      title={name}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        // Rounded square (matches the Rhythm board), not a circle.
        borderRadius: Math.max(6, Math.round(size * 0.28)),
        background: fill ?? 'var(--surface-2)',
        color: fill ? 'var(--surface)' : 'var(--text-2)',
        fontSize: Math.round(size * 0.4),
        fontWeight: 600,
        border: fill ? 'none' : '1px solid var(--border)',
        userSelect: 'none',
        flexShrink: 0,
      }}
    >
      {initials}
    </span>
  )
}
